import { useCallback, useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import { UserProfile } from 'types/user';
import { usersApi } from './usersApi';

const globalProfileCache = new Map<string, UserProfile>();

// Smart hook that avoids duplicate API calls by checking global cache first
export const useUserProfiles = (cwUserIds: string[]) => {
  const uniqueIds = useMemo(() => Array.from(new Set(cwUserIds)), [cwUserIds]);

  // Determine what we need to fetch vs what we have cached
  const { missingIds, cachedProfiles } = useMemo(() => {
    const missing: string[] = [];
    const cached: UserProfile[] = [];

    uniqueIds.forEach(id => {
      const profile = globalProfileCache.get(id);
      if (profile) {
        cached.push(profile);
      } else {
        missing.push(id);
      }
    });

    return { missingIds: missing, cachedProfiles: cached };
  }, [uniqueIds]);

  // Only create SWR key for missing profiles
  const fetchKey = missingIds.length > 0 ? `fetch-missing-${missingIds.sort().join(',')}` : null;

  const { data: fetchedProfiles } = useSWR(
    fetchKey,
    async () => {
      const result = await usersApi.userProfiles.list(missingIds);

      // Update both global cache and individual SWR caches
      result.values.forEach(profile => {
        globalProfileCache.set(profile.cwUserId, profile);
        mutate(`user-profile-${profile.cwUserId}`, profile, false);
      });

      return result.values;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  );

  // Combine cached and newly fetched profiles
  const allProfiles = useMemo(() => {
    return [...cachedProfiles, ...(fetchedProfiles || [])];
  }, [cachedProfiles, fetchedProfiles]);

  // Return profiles in the same order as requested
  return useMemo(() => {
    const profileMap = new Map(allProfiles.map(p => [p.cwUserId, p]));
    return cwUserIds.map(id => profileMap.get(id)).filter(Boolean) as UserProfile[];
  }, [allProfiles, cwUserIds]);
};

const useUserProfilesUtils = () => {
  // Invalidate profiles from cache
  const invalidateProfiles = useCallback((cwUserIds?: string[]) => {
    if (cwUserIds) {
      // Invalidate specific profiles from both SWR and global cache
      cwUserIds.forEach(id => {
        globalProfileCache.delete(id);
        mutate(`user-profile-${id}`);
      });
      // Also invalidate any fetch caches that might contain these profiles
      mutate(key => typeof key === 'string' && key.startsWith('fetch-missing-'));
    } else {
      // Invalidate all profiles from both caches
      globalProfileCache.clear();
      mutate(key => typeof key === 'string' && (key.startsWith('user-profile-') || key.startsWith('fetch-missing-')));
    }
  }, []);

  return {
    invalidateProfiles,
  };
};

// For backwards compatibility
export const useUserProfilesInvalidate = () => {
  const { invalidateProfiles } = useUserProfilesUtils();
  return invalidateProfiles;
};
